/**
 * Shared access-token refresh — used by BOTH axios interceptors
 * (V2 global in Route/index.jsx and the V3 `api` instance in useApi.js).
 *
 * Why this exists:
 *   The backend issues a 15-minute access token plus a 7-day refresh token
 *   (authController.login). Before this, the login flow dropped the refresh
 *   token and neither interceptor renewed the access token, so every session
 *   hard-expired ~15 min in with a "Session has ended" logout. Centralising
 *   the refresh here (one in-flight POST shared across all failing requests)
 *   lets both interceptors silently renew instead of logging the user out.
 *
 * It also distinguishes the two kinds of 403 the API returns:
 *   - auth failure   (expired/invalid/missing token)  → refresh, else expire
 *   - authorization  (rbac "Missing permission")       → NOT a session problem
 * so a single forbidden sub-request no longer logs the user out of the app.
 */
import axios from 'axios';
import config from '../../../config';

// Bare client with NO interceptors — the refresh POST must not re-enter the
// global/V3 response interceptors (a failing refresh would otherwise try to
// refresh itself, recursing).
const bare = axios.create();

let refreshing = null;

/**
 * Returns a fresh access token (string) or null if refresh isn't possible
 * (no refresh token stored, or the refresh endpoint rejected it). Concurrent
 * callers share one in-flight request.
 */
export function refreshAccessToken() {
    if (refreshing) return refreshing;
    const refresh = (typeof window !== 'undefined') && localStorage.getItem('refresh_token');
    if (!refresh) return Promise.resolve(null);

    refreshing = bare
        .post(`${config.hostname}/token/refresh`, { refresh_token: refresh })
        .then((res) => {
            const token = res && res.data && res.data.token;
            if (!token) return null;
            localStorage.setItem('sessiontoken', token);
            if (res.data.refresh_token) localStorage.setItem('refresh_token', res.data.refresh_token);
            return token;
        })
        .catch(() => null) // 401/403/network → treat as unrefreshable
        .finally(() => { refreshing = null; });

    return refreshing;
}

/**
 * True when a 403 is an rbac authorization denial (the user is authenticated
 * but lacks permission for THAT resource) rather than an auth failure. Such a
 * response must not end the session — only deny the one action.
 *
 * rbac.js denials carry a JSON body ({ error: 'Forbidden', detail: 'Missing
 * permission: …' } or { error: 'User not found' }); an expired-token 403 from
 * auth.js is `res.sendStatus(403)` (a bare "Forbidden" string body).
 */
export function isAuthorizationDenied(error) {
    const status = error && error.response && error.response.status;
    if (status !== 403) return false;
    const data = error.response.data;
    if (!data || typeof data !== 'object') return false;
    return Boolean(data.detail) || data.error === 'Forbidden' || data.error === 'User not found';
}
